/**
 * @file billSupplierDefault.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-07
 * @modified 2026-10-07
 * @description Which supplier a new bill starts with (Cam, 2026-10-07).
 *
 * From an uploaded invoice: the supplier NAMED ON THE INVOICE, matched to a
 * contact (POST /api/bills/parse-pdf → suggested.supplier_match) — not the
 * matched PO's supplier. When the name matches no contact, the PO's supplier
 * is used instead — its supplier group's PARENT when the PO is a child's
 * (MVFS - Lifton, Risdon… are billed by Mole Valley Feed Solutions; the
 * server sends it as matched_po.bill_supplier) — and the supplier box is
 * marked to be checked until the user picks or confirms one.
 *
 * From POs with no invoice (Add Bill from a PO, or several POs together):
 * the server's generate-from-pos says when it defaulted to a group's parent
 * (`supplier_default.needs_check`), which is marked the same way.
 *
 * A supplier the invoice names that is a different supplier from the PO's
 * (not one supplier group — utils/billSupplierMatch.supplierClash) is not
 * decided here: the reconciler asks the user, and this returns the PO's own
 * supplier as shown there, which a "use the invoice's" answer then replaces.
 */

import { supplierClash } from './billSupplierMatch';

/** The hint under the supplier box when the invoice did not name a supplier we know. */
export const SUPPLIER_NOT_ON_INVOICE_HINT = 'Supplier not found on the invoice — taken from the PO; check it';

/** The hint when a bill started from POs defaulted to their supplier group's parent. */
export const SUPPLIER_FROM_GROUP_HINT = "Supplier taken from the PO's supplier group — check it";

const asSupplier = (accountId, name, xeroContactId) => ({
  accountId: accountId != null && accountId !== '' ? String(accountId) : '',
  name: name || '',
  xeroContactId: xeroContactId || '',
});

/**
 * The supplier an uploaded invoice gives the bill, or null to leave it.
 *
 * @param {object|null} match — suggested.supplier_match
 *        { account_id, company_name, xero_contact_id, group_account_ids, printed_name }
 * @param {object|null} po — the PO's supplier: suggested.matched_po
 *        { account_id, supplier_name, xero_contact_id, bill_supplier? }, or the
 *        bill's current supplier when it was started from POs
 * @returns {null | { accountId: string, name: string, xeroContactId: string,
 *   needsCheck: boolean, hint: string, source: 'invoice'|'po'|'po_chosen' }}
 */
export function supplierFromUpload(match, po) {
  const hasPo = !!(po && (po.account_id || po.supplier_name));
  if (match && match.account_id) {
    // A different supplier from the PO's: the reconciler asks; start from the PO's.
    if (hasPo && supplierClash(match, { accountId: po.account_id, name: po.supplier_name })) {
      return { ...asSupplier(po.account_id, po.supplier_name, po.xero_contact_id),
        needsCheck: false, hint: '', source: 'po_chosen' };
    }
    return { ...asSupplier(match.account_id, match.company_name, match.xero_contact_id),
      needsCheck: false, hint: '', source: 'invoice' };
  }
  if (!hasPo) return null;
  const b = po.bill_supplier && po.bill_supplier.account_id ? po.bill_supplier : null;
  const chosen = b
    ? asSupplier(b.account_id, b.company_name, b.xero_contact_id)
    : asSupplier(po.account_id, po.supplier_name, po.xero_contact_id);
  return { ...chosen, needsCheck: true, hint: SUPPLIER_NOT_ON_INVOICE_HINT, source: 'po' };
}

/**
 * The check hint for a bill generated from POs (generate-from-pos), or ''.
 * @param {object|null} generated — the server's bill: { supplier_default }
 */
export function supplierCheckFromPos(generated) {
  return generated?.supplier_default?.needs_check ? SUPPLIER_FROM_GROUP_HINT : '';
}
